import React from 'react'
import {Box, Text, List, ListItem, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import {UnorderedList} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useLocation} from 'react-router-dom'
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url'

export const GiftCallout = () => {
    const {locale, site, buildUrl} = useMultiSite()
    const location = useLocation()

    const isCA = locale?.id?.includes('CA') || locale?.alias?.toLowerCase().includes('ca')
    const isUS = locale?.id?.includes('US') || locale?.alias?.toLowerCase().includes('us')

    // Build cross-locale link to the equivalent of the current page in another region.
    // Returns null when the target region's locale isn't supported by the current site
    // (e.g. on a CA-only MRT instance, requesting US won't resolve).
    const buildCrossLocaleLink = (targetRegion) => {
        const supportedLocales = site?.l10n?.supportedLocales || []
        const targetLocale = supportedLocales.find((l) => {
            const id = typeof l === 'string' ? l : l?.id || ''
            return id.toUpperCase().includes(targetRegion)
        })
        if (!targetLocale) return null
        const targetId = typeof targetLocale === 'string' ? targetLocale : targetLocale.id
        return getPathWithLocale(targetId, buildUrl, {location})
    }

    if (isCA) {
        return (
            <Box mx={2} className="notes">
                <Text textStyle={'labelXs'} mb={2}>
                    <FormattedMessage id="gift_callout.note.label" defaultMessage="Notes" />
                </Text>
                <Text textStyle="bodyXsRegular" mt={2}>
                    <FormattedMessage
                        id="gift_callout.note.ca_currency"
                        defaultMessage="This gift card will be in <bold>CAD</bold>. To purchase in <bold>USD</bold> click {link}."
                        values={{
                            link: (
                                <Link href={buildCrossLocaleLink('US')} textDecoration="underline">
                                    <FormattedMessage
                                        id="gift_callout.note.currency.link"
                                        defaultMessage="here"
                                    />
                                </Link>
                            ),
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </Text>
                <Text textStyle="bodyXsRegular" mt={1}>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </Text>
            </Box>
        )
    }

    if (isUS) {
        return (
            <Box mt={2} className="notes">
                <Text textStyle={'labelXs'} mb={2}>
                    <FormattedMessage id="gift_callout.note.label" defaultMessage="Notes" />
                </Text>
                <Text textStyle="bodyXsRegular" mt={2}>
                    <FormattedMessage
                        id="gift_callout.note.us_currency"
                        defaultMessage="This gift card will be in <bold>USD</bold>. To purchase in <bold>CAD</bold> click {link}."
                        values={{
                            link: (
                                <Link href={buildCrossLocaleLink('CA')} textDecoration="underline">
                                    <FormattedMessage
                                        id="gift_callout.note.currency.link"
                                        defaultMessage="here"
                                    />
                                </Link>
                            ),
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </Text>
                <Text textStyle="bodyXsRegular" mt={1}>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </Text>
            </Box>
        )
    }

    return (
        <Box bg="alternativeWarning.100" color="warningText" p={4} fontSize="sm">
            <Text fontWeight="bold">
                <FormattedMessage id="gift_callout.note.label" defaultMessage="Note:" />
            </Text>
            <UnorderedList pl={2}>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.uk_only"
                        defaultMessage="This voucher can only be used in the <bold>United Kingdom</bold> in <bold>£ GBP</bold>."
                        values={{
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </ListItem>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </ListItem>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.final_sale"
                        defaultMessage="This item is <bold>final sale</bold> and not eligible for points."
                        values={{
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </ListItem>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.cancellation_waiver"
                        defaultMessage="By purchasing an e-voucher, you are consenting to immediate delivery and acknowledge that you are waiving your right to cancellation. Your statutory rights are not affected."
                    />
                </ListItem>
            </UnorderedList>
        </Box>
    )
}
